import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { testimonials } from '../data/mockData';
import { trainingClasses } from '../data/trainingClasses';
import { Star, Quote, Check, ArrowRight, Clock } from 'lucide-react';
import Avatar from '../components/Avatar';
import CountdownTimer from '../components/CountdownTimer';

const Risultati = () => {
  // Target date: 7 days from now (loops automatically)
  const targetDate = new Date();
  targetDate.setDate(targetDate.getDate() + 7);

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1583454110551-21f2fa2afe61"
            alt="Success"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-transparent"></div>
        </div>

        <div className="relative container mx-auto px-4 py-32">
          <div className="max-w-3xl text-center mx-auto">
            <h1 className="text-5xl md:text-7xl font-black text-white mb-6 leading-tight">
              Risultati <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-green-400">Veri</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
              Persone reali. Trasformazioni reali. Risultati che parlano più forte di qualsiasi promessa.
            </p>
          </div>
        </div>
      </section>

      {/* Training Classes Section */}
      <section className="py-24 bg-gradient-to-b from-black to-zinc-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-black text-white mb-6">
              Le Nostre <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-green-400">Classi</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Scegli il programma di allenamento più adatto ai tuoi obiettivi. Ogni classe è progettata per massimizzare i risultati.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {trainingClasses.map((trainingClass) => (
              <Card key={trainingClass.id} className="bg-white/5 border border-white/10 backdrop-blur-sm hover:border-orange-500/50 transition-all duration-300 group flex flex-col">
                <CardHeader>
                  {trainingClass.badge && (
                    <div className="inline-block mb-3">
                      <span className="px-3 py-1 bg-orange-500/20 border border-orange-500/30 rounded-full text-orange-500 font-bold text-xs uppercase tracking-widest">
                        {trainingClass.badge}
                      </span>
                    </div>
                  )}
                  <CardTitle className="text-2xl font-black text-white mb-4">
                    {trainingClass.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  <div className="relative h-48 -mx-6 -mt-2 mb-6 overflow-hidden rounded-t-lg">
                    <img
                      src={trainingClass.image}
                      alt={trainingClass.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                  </div>

                  <p className="text-gray-300 mb-6 leading-relaxed">
                    {trainingClass.description}
                  </p>

                  <div className="space-y-3 mb-6 flex-1">
                    {trainingClass.benefits.map((benefit, i) => (
                      <div key={`${trainingClass.id}-benefit-${i}`} className="flex items-start space-x-3">
                        <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-400 text-sm">{benefit}</span>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-white/10 pt-4 space-y-2 text-sm text-gray-400">
                    <div className="flex justify-between">
                      <span>Durata:</span>
                      <span className="text-white font-semibold">{trainingClass.details.duration}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Frequenza:</span>
                      <span className="text-white font-semibold">{trainingClass.details.frequency}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Livello:</span>
                      <span className="text-white font-semibold">{trainingClass.details.level}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12 px-2">
            <Link to="/contatti" className="inline-block w-full sm:w-auto">
              <Button className="w-full sm:w-auto bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base uppercase tracking-wide whitespace-normal">
                Prenota la Tua Classe
                <ArrowRight className="ml-2 w-5 h-5 flex-shrink-0" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Countdown Prova Gratuita Section */}
      <section className="py-16 sm:py-24 bg-zinc-950 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto text-center">
            <div className="mb-6 sm:mb-8 px-2">
              <Clock className="w-12 h-12 sm:w-16 sm:h-16 text-orange-500 mx-auto mb-4 sm:mb-6" />
              <h2 className="text-3xl sm:text-4xl md:text-6xl font-black text-white mb-4 sm:mb-6">
                Offerta <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-green-400">Limitata</span>
              </h2>
              <p className="text-base sm:text-xl text-gray-400 max-w-2xl mx-auto mb-8 sm:mb-12">
                Prenota ora la tua prova gratuita. Posti limitati disponibili questa settimana.
              </p>
            </div>

            <div className="px-2">
              <CountdownTimer targetDate={targetDate} />
            </div>

            <div className="mt-8 sm:mt-12 px-2">
              <Link to="/contatti" className="inline-block w-full sm:w-auto">
                <Button className="w-full sm:w-auto bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold px-8 sm:px-12 py-4 sm:py-8 text-base sm:text-lg uppercase tracking-wide shadow-2xl hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105">
                  Prenota Prova Gratuita Ora
                  <ArrowRight className="ml-2 sm:ml-3 w-5 h-5 sm:w-6 sm:h-6" />
                </Button>
              </Link>
              <p className="text-xs sm:text-sm text-gray-500 mt-4 sm:mt-6">
                Nessun impegno. Nessun costo nascosto. Solo 60 minuti per scoprire se Performove fa per te.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-gradient-to-b from-zinc-950 to-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-black text-white mb-6">
              Cosa Dicono i <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-green-400">Nostri Clienti</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Recensioni autentiche da Google My Business. Persone reali, risultati reali.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="bg-white/5 border border-white/10 backdrop-blur-sm hover:border-orange-500/50 transition-all duration-300 group">
                <CardContent className="p-8">
                  <Quote className="w-10 h-10 text-orange-500 mb-4" />
                  
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={`star-${i}`} className="w-5 h-5 fill-orange-500 text-orange-500" />
                    ))}
                  </div>

                  <p className="text-gray-300 mb-6 leading-relaxed italic">
                    "{testimonial.text}"
                  </p>

                  <div className="flex items-center space-x-4 pt-4 border-t border-white/10">
                    <Avatar initials={testimonial.initials} className="w-12 h-12 text-lg" />
                    <div>
                      <div className="font-bold text-white">{testimonial.name}</div>
                      <div className="text-sm text-gray-500">{testimonial.source} Review</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Risultati;
